import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Input, DatePicker, Image, Row, Col, notification, Typography, Space, InputNumber } from 'antd';
import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import vn from 'antd/es/date-picker/locale/vi_VN';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import FormLabel from '../../../components/FormLabel';
import CustomSelect from '../../../components/CustomSelect';
import AudienceRuleEditor from '../../../components/AudienceRuleEditor';
import NormalFormLabel from '../../../components/NormalFormLabel';
import TextArea from 'antd/es/input/TextArea';
import {
  LIST_PLATFORM,
  LIST_POPUP_CONTENT_TYPE,
  LIST_POPUP_TYPE,
  LIST_STATUS_POPUP,
} from '../../../constants/appSelect';
import { LPD_banner, IMG_banner, MREC_banner } from '../../../assets/banner_image/banner_image';
import { getPopupDetailRequest, createPopupRequest } from '../../../services/popupRequest';
import { getListAudienceRequest } from '../../../services/audianceRequest';
import { useMutation, useQuery } from '@tanstack/react-query';
import { checkPermission } from '../../../utils/checkPermission';
import { CREATE_UPDATE_POPUP, GET_POPUP_DETAIL } from '../../../constants/apiEndPoint';
import { getListPopupPageRequest, getListVersionAndTopicRequest } from '../../../services/commonRequest';
dayjs.extend(buddhistEra);

const SaoChepPopup = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Metadata đã lưu của bản ghi này theo từng loại nội dung — đổi loại thì hiện đúng metadata của loại đó
  const metadataByContentType = useRef({});
  const prevContentType = useRef();
  const handleChangeContentType = (value) => {
    const prev = prevContentType.current;
    metadataByContentType.current[prev] = form.getFieldValue('metadata');
    prevContentType.current = value;
    if (value === 'KHNLCN' || prev === 'KHNLCN' || metadataByContentType.current[value] !== undefined) {
      form.setFieldsValue({ metadata: metadataByContentType.current[value] });
    }
  };
  const { popupId } = useParams();
  const watchTypeTopicValue = Form.useWatch('type_topic', form);
  const customerGroupValue = Form.useWatch('audience', form);
  const outsideCustomerGroupValue = Form.useWatch('not_audience', form);
  const contentValue = Form.useWatch('content_type', form);
  const checkBirthdayValue = Form.useWatch('show_on_birthday', form);

  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');

  const handleAddVersion = () => {
    const newValue = inputValue.trim();
    if (!newValue) return;

    const exists = options.some((option) => option.value === newValue);
    const newOptions = exists ? options : [...options, { label: newValue, value: newValue }];
    setOptions(newOptions);

    const currentValues = form.getFieldValue('version') || [];
    const newValues = Array.from(new Set([...currentValues, newValue]));
    form.setFieldsValue({ version: newValues });

    setInputValue('');
  };

  const backToPage = () => {
    navigate('/quan-ly-popup');
  };

  // get list audience
  const { data: audienceList } = useQuery({
    queryKey: ['audienceList'],
    queryFn: () => getListAudienceRequest(),
  });

  // convert data audience options
  const dataAudienceOptions = useMemo(() => {
    const options =
      audienceList?.map((item) => {
        return { label: item?.name, value: item?.code };
      }) || [];
    return options;
  }, [audienceList]);

  // get list version
  const { data: dataAllVersions } = useQuery({
    queryKey: ['dataAllVersions'],
    queryFn: () => getListVersionAndTopicRequest({ type: 2 }),
  });

  // convert list topic options
  useEffect(() => {
    const options =
      dataAllVersions?.map((item) => {
        return { label: item?.name, value: item?.value };
      }) || [];
    setOptions(options);
  }, [dataAllVersions]);

  // get list topic
  const { data: dataAllTopics } = useQuery({
    queryKey: ['dataAllTopics'],
    queryFn: () => getListVersionAndTopicRequest({ type: 3 }),
  });

  // convert list topic options
  const dataAllTopicOptions = useMemo(() => {
    const options =
      dataAllTopics?.map((item) => {
        return { label: item?.name, value: item?.value };
      }) || [];
    return options;
  }, [dataAllTopics]);

  // get data popups page
  const { data: dataAllPopupPage } = useQuery({
    queryKey: ['dataAllPopupPage'],
    queryFn: () => getListPopupPageRequest(),
  });

  // convert list popup page options
  const dataAllPopupPageOption = useMemo(() => {
    const options =
      dataAllPopupPage?.map((item) => {
        return { label: item?.name, value: item?.key };
      }) || [];
    return options;
  }, [dataAllPopupPage]);

  const filteredOutsideCustomerGroupOptions = useMemo(() => {
    return dataAudienceOptions.filter((option) => !customerGroupValue?.includes(option.value));
  }, [customerGroupValue, dataAudienceOptions]);

  const filteredCustomerGroupOptions = useMemo(() => {
    return dataAudienceOptions.filter((option) => !outsideCustomerGroupValue?.includes(option.value));
  }, [outsideCustomerGroupValue, dataAudienceOptions]);

  const { data: popupDetail } = useQuery({
    queryKey: ['popupDetail', popupId],
    queryFn: () => getPopupDetailRequest(popupId),
    enabled: !!popupId && checkPermission(GET_POPUP_DETAIL, 'Xem chi tiết'),
  });

  useEffect(() => {
    if (popupDetail) {
      metadataByContentType.current = { [popupDetail?.content_type]: popupDetail?.metadata };
      prevContentType.current = popupDetail?.content_type;
      form.setFieldsValue({
        campaign_id: popupDetail?.campaign_id, // id chiến dịch
        popup_type: popupDetail?.popup_type, // loại popup
        page: popupDetail?.page?.[0], // vị trí hiển thị
        type_topic: popupDetail?.type_topic, //Loại tập khách hàng
        platform: popupDetail?.platform, //Nền tảng
        version: popupDetail?.version, //Version
        topic: popupDetail?.topic, //Topic
        audience: popupDetail?.audience, //Tập khách hàng
        not_audience: popupDetail?.not_audience, //Ngoài tập khách hàng
        audience_rule: popupDetail?.audience_rule || null, //Quy tắc audience DNF
        title: popupDetail?.title, // tiêu đề
        description: popupDetail?.description, // mô tả
        partner_url: popupDetail?.partner_url, // link popup
        image: popupDetail?.image, // link ảnh
        content_type: popupDetail?.content_type, // loại nội dung
        metadata: popupDetail?.metadata, // nội dung html
        popup_description: popupDetail?.popup_description, // mô tả popup description
        publish_time: dayjs(popupDetail?.publish_time), // thời gian bắt đầu
        end_time: dayjs(popupDetail?.end_time), // thời gian kết thúc
        display_interval_by_day: popupDetail?.display_interval_by_day || 0, // khoảng cách giữa 2 lần hiện tính theo ngày
        can_close_after_sec: popupDetail?.can_close_after_sec || null, // thời gian hiện nút close (giây)
        can_close_after_sec_from_second_time: popupDetail?.can_close_after_sec_from_second_time || null, // thời gian hiện nút close từ lần hiển thị thứ 2 (giây)
        other_rule_show_in_client: popupDetail?.other_rule_show_in_client || '', // các hàm check điều kiện khác
        required_cache_img_before_show: popupDetail?.required_cache_img_before_show || 0, // cache ảnh trước khi hiển thị
        la_qc_kiem_tien: popupDetail?.la_qc_kiem_tien, // quảng cáo kiếm tiền
        max_click: popupDetail?.max_click, // số click tối đa
        max_impression: popupDetail?.max_impression, // số impression tối đa
        max_click_per_date: popupDetail?.max_click_per_date, // số click tối đa/ngày
        max_impression_per_date: popupDetail?.max_impression_per_date, // số impression tối đa/ngày
        show_on_birthday: popupDetail?.show_on_birthday || 0, // kiểm tra hiển thị đúng vào ngày sinh nhật
        date_from_receive: popupDetail?.date_from_receive, // số ngày hiển thị popup kể từ ngày sinh nhật
        place: popupDetail?.place, // thứ tự hiển thị
        status: popupDetail?.status, // trang thái
        max_impressions_per_account: popupDetail?.max_impressions_per_account,
        max_clicks_per_account: popupDetail?.max_clicks_per_account,
        display_interval_by_session: popupDetail?.display_interval_by_session,
        not_cache_in_client: popupDetail?.not_cache_in_client,
        sub_place: popupDetail?.sub_place,
      });
    }
  }, [popupDetail, form]);

  const { mutate: mutateCopyPopup, isPending: isLoadingCopyPopup } = useMutation({
    mutationFn: createPopupRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Sao chép popup thành công',
        });
        backToPage();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const buddhistLocale = {
    ...vn,
    lang: {
      ...vn.lang,
      fieldDateFormat: 'YYYY-MM-DD',
      fieldDateTimeFormat: 'YYYY-MM-DD HH:mm',
      yearFormat: 'YYYY',
      cellYearFormat: 'YYYY',
    },
  };

  const getImageBanner = () => {
    if (contentValue === 'LDP') {
      return <Image width={200} src={LPD_banner} />;
    } else if (contentValue === 'Mrec') {
      return <Image width={200} src={MREC_banner} />;
    } else if (contentValue === 'Image') {
      return <Image width={200} src={IMG_banner} />;
    } else {
      return null;
    }
  };

  const handleSubmitForm = (data) => {
    const dataSubmit = {
      id: null,
      group_id: popupDetail?.group_id, // id nhóm
      campaign_id: popupDetail?.campaign_id, // id chiến dịch
      popup_type: data?.popup_type, // loại popup
      page: data?.page ? [data?.page] : [], // vị trí hiển thị
      type_topic: data?.type_topic, //Loại tập khách hàng
      platform: data?.platform, //Nền tảng
      version: data?.version || [], //Version
      topic: data?.topic || [], //Topic
      audience: data?.audience || [], //Tập khách hàng
      not_audience: data?.not_audience || [], //Ngoài tập khách hàng
      audience_rule: data?.audience_rule || null, //Quy tắc audience DNF
      title: data?.title, // tiêu đề
      description: data?.description, // mô tả
      partner_url: popupDetail?.partner_url, // link popup
      image: data?.image, // link ảnh
      content_type: data?.content_type, // loại nội dung
      metadata: data?.metadata, // nội dung html
      popup_description: data?.popup_description, // mô tả popup description
      publish_time: data?.publish_time, // thời gian bắt đầu
      end_time: data?.end_time, // thời gian kết thúc
      required_cache_img_before_show: data?.required_cache_img_before_show, // cache ảnh trước khi hiển thị
      display_interval_by_day: data?.display_interval_by_day, // khoảng cách giữa 2 lần hiện tính theo ngày
      can_close_after_sec: data?.can_close_after_sec, // thời gian hiện nút close (giây)
      can_close_after_sec_from_second_time: data?.can_close_after_sec_from_second_time, // thời gian hiện nút close từ lần hiển thị thứ 2 (giây)
      other_rule_show_in_client: data?.other_rule_show_in_client, // các hàm check điều kiện khác
      la_qc_kiem_tien: data?.la_qc_kiem_tien, // quảng cáo kiếm tiền
      max_click: data?.max_click, // số click tối đa
      max_impression: data?.max_impression, // số impression tối đa
      max_click_per_date: data?.max_click_per_date, // số click tối đa/ngày
      max_impression_per_date: data?.max_impression_per_date, // số impression tối đa/ngày
      partner_id: popupDetail?.partner_id, // id đối tác
      show_on_birthday: data?.show_on_birthday, // kiểm tra hiển thị đúng vào ngày sinh nhật
      date_from_receive: data?.date_from_receive, // số ngày hiển thị popup kể từ ngày sinh nhật
      place: data?.place, // thứ tự hiển thị
      status: data?.status, // trang thái
      max_impressions_per_account: data?.max_impressions_per_account,
      max_clicks_per_account: data?.max_clicks_per_account,
      display_interval_by_session: data?.display_interval_by_session,
      not_cache_in_client: data?.not_cache_in_client,
      sub_place: data?.sub_place,
    };
    mutateCopyPopup(dataSubmit);
  };

  return (
    <div className="w-full">
      {checkPermission(CREATE_UPDATE_POPUP, 'Sao chép') && checkPermission(GET_POPUP_DETAIL, 'Xem chi tiết') && (
        <>
          <h1 className="font-semibold text-[24px] leading-[32px] gap-[10px] py-[10px]">Thêm popup</h1>

          <Form
            name="copy-popup"
            layout="vertical"
            requiredMark={false}
            form={form}
            onFinish={handleSubmitForm}
            scrollToFirstError={{ behavior: 'smooth', block: 'center', focus: true }}
          >
            <Row gutter={[16, 0]}>
              <Col xs={12}>
                <Form.Item label={<FormLabel text="Chiến dịch quảng cáo" />} name="campaign_id">
                  <CustomSelect
                    placeholder="Chọn chiến dịch quảng cáo"
                    disabled
                    options={[{ value: popupDetail?.campaign_id, label: popupDetail?.campaignName }]} // Chỉ hiển thị tên chiến dịch
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Loại popup" />}
                  name="popup_type"
                  rules={[{ required: true, message: 'Vui lòng chọn loại popup' }]}
                >
                  <CustomSelect placeholder="Chọn loại popup" options={LIST_POPUP_TYPE} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label={
                    <>
                      <FormLabel text="Vị trí hiển thị" />
                      Xem link preview các vị trí &nbsp;
                      <a
                        className="text-blue-600"
                        href="https://docs.google.com/spreadsheets/d/1YN7xizln-ajUjtE9Wn4uTv6GxrPwrTP-ohRk9zA9L4Y/edit?gid=1449111062#gid=1449111062"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        [Lịch Việt] List Banner quảng cáo
                      </a>
                    </>
                  }
                  name="page"
                  rules={[{ required: true, message: 'Vui lòng chọn vị trí hiển thị' }]}
                >
                  <CustomSelect placeholder="Chọn vị trí hiển thị" options={dataAllPopupPageOption} />
                </Form.Item>
              </Col>

              {/* <Col xs={12}>
                <Form.Item label={<FormLabel text="Loại tập khách hàng" />} name="type_topic" initialValue={1}>
                  <CustomSelect
                    placeholder="Chọn loại tập khách hàng"
                    allowClear
                    options={[
                      { value: 1, label: 'Topic' },
                      { value: 2, label: 'Tập khách hàng' },
                    ]}
                  />
                </Form.Item>
              </Col>

              {watchTypeTopicValue === 1 && (
                <> */}
              <Col xs={24}>
                <Form.Item label={<NormalFormLabel text="Nền tảng" />} name="platform">
                  <CustomSelect placeholder="Chọn nền tảng" allowClear options={LIST_PLATFORM} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item label={<NormalFormLabel text="Version" />} name="version">
                    <CustomSelect placeholder="Chọn version" allowClear mode="multiple" options={options} />
                  </Form.Item>
                  <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onPressEnter={handleAddVersion}
                    placeholder="Nhập version mới"
                  />
                  <Button onClick={handleAddVersion}>Thêm version</Button>
                  <Typography.Text>
                    Nếu phiên bản có dạng v9.0:v9.3 thì sẽ hiển thị cho các phiên bản từ 9.0 đến 9.3. Nếu phiên bản có
                    dạng v9.0:999 thì sẽ hiển thị cho các phiên bản từ 9.0 trở lên. Nếu phiên bản có dạng v9.0 thì sẽ
                    hiển thị cho phiên bản 9.0. Nếu phiên bản có dạng 0:v9.0 thì sẽ hiển thị cho các phiên bản từ 9.0
                    trở xuống
                  </Typography.Text>
                </Space>
              </Col>
              <Col xs={24}>
                <Form.Item label={<NormalFormLabel text="Topic" />} name="topic">
                  <CustomSelect placeholder="Chọn topic" allowClear mode="multiple" options={dataAllTopicOptions} />
                </Form.Item>
              </Col>
              {/* </>
              )}

              {watchTypeTopicValue === 2 && (
                <> */}
              <Col xs={24}>
                <Form.Item label={<NormalFormLabel text="Tập khách hàng" />} name="audience">
                  <CustomSelect
                    placeholder="Chọn tập khách hàng"
                    allowClear
                    mode="multiple"
                    options={filteredCustomerGroupOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item label={<NormalFormLabel text="Quy tắc audience (AND/OR)" />} name="audience_rule">
                  <AudienceRuleEditor audienceOptions={dataAudienceOptions} />
                </Form.Item>
              </Col>
              {/* <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Ngoài tập khách hàng" />} name="not_audience">
                  <CustomSelect
                    placeholder="Chọn ngoài tập khách hàng"
                    allowClear
                    mode="multiple"
                    options={filteredOutsideCustomerGroupOptions}
                  />
                </Form.Item>
              </Col>
              </>
              )} */}

              <Col xs={24}>
                <Form.Item
                  label={<FormLabel text="Tiêu đề" />}
                  rules={[{ required: true, message: 'Vui lòng nhập tiêu đề popup' }]}
                  name="title"
                >
                  <Input placeholder="Nhập tiêu đề popup..." />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label={<NormalFormLabel text="Mô tả" />} name="description">
                  <Input placeholder="Nhập mô tả..." />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label={<FormLabel text="Link popup" />} name="partner_url">
                  <Input placeholder="Nhập link popup..." disabled />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label={<FormLabel text="Ảnh popup" />} name="image">
                  <div className="gap-6">
                    <div>{popupDetail?.image}</div>
                    <Image width={200} src={popupDetail?.image} />
                  </div>
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Loại nội dung" />}
                  rules={[{ required: true, message: 'Vui lòng chọn loại nội dung' }]}
                  name="content_type"
                >
                  <CustomSelect
                    placeholder="Chọn loại nội dung"
                    options={LIST_POPUP_CONTENT_TYPE}
                    onChange={handleChangeContentType}
                  />
                </Form.Item>
                {getImageBanner()}
              </Col>
              <Col xs={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {['LDP', 'Video', 'LDPNavigation', 'KHNLCN'].includes(contentValue) && (
                    <Form.Item
                      label={<FormLabel text="Nội dung html (Metadata)" />}
                      rules={[
                        {
                          required: ['LDP', 'Video', 'LDPNavigation', 'KHNLCN'].includes(contentValue),
                          message: 'Vui lòng nhập nội dung html',
                        },
                      ]}
                      name="metadata"
                    >
                      <TextArea rows={7} placeholder="Nhập nội dung html" />
                    </Form.Item>
                  )}
                  <Form.Item label={<NormalFormLabel text="Popup description" />} name="popup_description">
                    <Input placeholder="Nhập mô tả popup description..." />
                  </Form.Item>
                </Space>
              </Col>

              <Col xs={12}>
                <Form.Item label={<FormLabel text="Thời gian bắt đầu" />} name="publish_time">
                  <DatePicker
                    format="HH:mm DD/MM/YYYY"
                    locale={buddhistLocale}
                    showTime
                    needConfirm={false}
                    disabled
                    style={{ width: '100%', height: '40px', backgroundColor: '#E0E2E7' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item label={<FormLabel text="Thời gian kết thúc" />} name="end_time">
                  <DatePicker
                    format="HH:mm DD/MM/YYYY"
                    showTime
                    locale={buddhistLocale}
                    disabled
                    needConfirm={false}
                    style={{ width: '100%', height: '40px', backgroundColor: '#E0E2E7' }}
                  />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Khoảng cách giữa 2 lần hiện tính theo ngày" />}
                  name="display_interval_by_day"
                  rules={[{ required: true, message: 'Vui lòng nhập khoảng cách giữa 2 lần hiện tính theo ngày' }]}
                >
                  <InputNumber min={0} placeholder="Nhập khoảng cách giữa 2 lần hiện tính theo ngày..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  label={<NormalFormLabel text="Thời gian hiện nút close (giây)" />}
                  name="can_close_after_sec"
                  rules={[
                    {
                      pattern: /^[0-9]\d*$/,
                      message: 'Thời gian hiện nút close (giây) phải là số nguyên dương',
                    },
                  ]}
                >
                  <InputNumber type="number" min={0} placeholder="Nhập thời gian hiện nút close (giây)..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  label={<NormalFormLabel text="Thời gian hiện nút close từ lần hiển thị thứ 2 (giây)" />}
                  name="can_close_after_sec_from_second_time"
                  rules={[
                    {
                      pattern: /^[0-9]\d*$/,
                      message: 'Thời gian hiện nút close từ lần hiển thị thứ 2 (giây) phải là số nguyên dương',
                    },
                  ]}
                >
                  <InputNumber type="number" min={0} placeholder="Nhập thời gian hiện nút close từ lần hiển thị thứ 2 (giây)..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Các hàm check điều kiện khác" />} name="other_rule_show_in_client">
                  <Input placeholder="Nhập các hàm check điều kiện khác..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Cache ảnh trước khi hiển thị" />}
                  rules={[{ required: true, message: 'Vui lòng chọn cache ảnh trước khi hiển thị' }]}
                  name="required_cache_img_before_show"
                >
                  <CustomSelect
                    placeholder="Chọn cache ảnh trước khi hiển thị"
                    options={[
                      { value: 0, label: 'Tắt' },
                      { value: 1, label: 'Bật' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Quảng cáo kiếm tiền " />}
                  rules={[{ required: true, message: 'Vui lòng chọn quảng cáo kiếm tiền' }]}
                  name="la_qc_kiem_tien"
                >
                  <CustomSelect
                    placeholder="Chọn quảng cáo kiếm tiền "
                    options={[
                      { value: 0, label: 'Tắt' },
                      { value: 1, label: 'Bật' },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Số click tối đa" />} name="max_click">
                  <InputNumber min={0} placeholder="Nhập số click tối đa..." />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Số impression tối đa" />} name="max_impression">
                  <InputNumber min={0} placeholder="Nhập số impression tối đa..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Số click tối đa/ngày" />} name="max_click_per_date">
                  <InputNumber min={0} placeholder="Nhập số click tối đa/ngày ..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Khoảng cách giữa 2 lần hiện tính theo session" />} name="display_interval_by_session">
                  <InputNumber min={0} placeholder="Khoảng cách giữa 2 lần hiện tính theo session" />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Số impression tối đa/ngày" />} name="max_impression_per_date">
                  <InputNumber min={0} placeholder="Nhập số impression tối đa/ngày ..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Số click tối đa/ tài khoản" />} name="max_clicks_per_account">
                  <InputNumber min={0} precision={0} className="w-full" placeholder="Nhập số click tối đa/ tài khoản" />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  label={<NormalFormLabel text="Số impression tối đa/ tài khoản" />}
                  name="max_impressions_per_account"
                >
                  <InputNumber
                    min={0}
                    precision={0}
                    className="w-full"
                    placeholder="Nhập số impression tối đa/ tài khoản"
                  />
                </Form.Item>
              </Col>

              <Col xs={checkBirthdayValue === 1 ? 12 : 24}>
                <Form.Item
                  label={<FormLabel text="Kiểm tra hiển thị đúng vào ngày sinh nhật" />}
                  rules={[{ required: true, message: 'Vui lòng chọn kiểm tra hiển thị đúng vào ngày sinh nhật' }]}
                  name="show_on_birthday"
                >
                  <CustomSelect
                    placeholder="Chọn kiểm tra hiển thị đúng vào ngày sinh nhật"
                    options={[
                      { value: 0, label: 'Không' },
                      { value: 1, label: 'Kiểm tra' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={checkBirthdayValue === 1 ? 12 : 0}>
                <Form.Item
                  label={<FormLabel text="Kể từ ngày nhận popup" />}
                  rules={[
                    {
                      required: checkBirthdayValue === 1,
                      message: 'Vui lòng nhập số ngày hiển thị popup kể từ ngày sinh nhật',
                    },
                  ]}
                  name="date_from_receive"
                >
                  <InputNumber min={0} placeholder="Nhập số ngày hiển thị popup kể từ ngày sinh nhật..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Thứ tự hiển thị" />}
                  rules={[{ required: true, message: 'Vui lòng nhập thứ tự hiển thị' }]}
                  name="place"
                >
                  <InputNumber min={1} placeholder="Nhập thứ tự hiển thị..." />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item label={<FormLabel text="Thứ tự trong vị trí" />} name="sub_place">
                  <InputNumber min={1} placeholder="Nhập thứ tự hiển thị trong cùng vị trí..." />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item label={<FormLabel text="Chế độ cache client" />} name="not_cache_in_client">
                  <CustomSelect
                    placeholder="Chọn chế độ cache ở client"
                    options={[
                      { value: 0, label: 'Cho phép cache (mặc định)' },
                      { value: 1, label: 'Không cache, luôn lấy mới' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Trạng thái" />}
                  rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
                  name="status"
                >
                  <CustomSelect placeholder="Chọn trạng thái" options={LIST_STATUS_POPUP} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <div className="flex justify-end gap-3">
                  <Form.Item>
                    <Button onClick={backToPage}>
                      <CloseOutlined /> Hủy bỏ
                    </Button>
                  </Form.Item>
                  <Form.Item>
                    <Button loading={isLoadingCopyPopup} type="primary" htmlType="submit">
                      <SaveOutlined /> Lưu lại
                    </Button>
                  </Form.Item>
                </div>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </div>
  );
};

export default SaoChepPopup;
